export type EcmChapterMeta = { order: number; duration: number };

/** Matches server rule: progress "at end" if time is within ~20s of catalog duration. */
export function isEcmChapterProgressComplete(
  currentTime: number,
  chapterDurationSeconds: number | undefined | null
): boolean {
  const d = Number(chapterDurationSeconds);
  if (!Number.isFinite(d) || d <= 0) {
    return false;
  }
  const t = Math.floor(Number(currentTime) || 0);
  return t >= d || Math.abs(t - d) <= 20;
}

/**
 * Mantine Stepper `active` index (aligned with `chapters.map` indices 0..n-1).
 * When the current catalog chapter is complete, advances toward the next step.
 * After the last chapter is complete, returns `chapters.length` so every step
 * including the last is `stepCompleted` (blue check), not still "in progress".
 */
export function deriveEcmStepperActiveStepIndex(input: {
  chapters: EcmChapterMeta[];
  current_section: number;
  current_time: number;
}): number {
  const { chapters, current_section, current_time } = input;
  if (chapters.length === 0) {
    return 0;
  }
  const idx = chapters.findIndex(ch => ch.order === current_section);
  if (idx < 0) {
    return Math.min(Math.max(0, current_section), chapters.length - 1);
  }
  const ch = chapters[idx];
  if (isEcmChapterProgressComplete(current_time, ch.duration)) {
    return Math.min(idx + 1, chapters.length);
  }
  return idx;
}

/** ECM video: free seek on past chapters, on completed current, or on next if prior is complete. */
export function canSeekEcmChapterVideo(input: {
  chapters: EcmChapterMeta[] | undefined;
  current_section: number;
  current_time: number;
  chapterOrder: number;
  chapterDuration: number | undefined;
}): boolean {
  const {
    chapters,
    current_section,
    current_time,
    chapterOrder,
    chapterDuration,
  } = input;
  const section = Number(current_section) || 0;
  if (chapterOrder < section) {
    return true;
  }
  if (chapterOrder === section) {
    return isEcmChapterProgressComplete(current_time, chapterDuration);
  }
  const activeMeta = chapters?.find(ch => ch.order === section);
  return (
    !!activeMeta &&
    isEcmChapterProgressComplete(current_time, activeMeta.duration)
  );
}
